import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { I18nProvider } from "./context/I18nContext";
import { ContentProvider } from "./context/ContentContext";
import { Toaster } from "sonner";
import Landing from "./pages/Landing";
import Pricing from "./pages/Pricing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import OrganizerOnboarding from "./pages/OrganizerOnboarding";
import OrganizerDashboard from "./pages/OrganizerDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import DemoPaddock from "./pages/DemoPaddock";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import PaddockList from "./pages/PaddockList";
import PaddockGame from "./pages/PaddockGame";
import GameBuilder from "./pages/GameBuilder";
import AuthCallback from "./pages/AuthCallback";
import VerifyEmail from "./pages/VerifyEmail";
import DailyStage from "./pages/DailyStage";
import DailyChallenge from "./pages/DailyChallenge";
import RallyLoveCard from "./pages/RallyLoveCard";
import OrganizerLoyalty from "./pages/OrganizerLoyalty";
import AdminDaily from "./pages/AdminDaily";
import AdminLoyalty from "./pages/AdminLoyalty";
import RallyFarm from "./pages/RallyFarm";
import FarmTeam from "./pages/FarmTeam";
import SeasonLeaderboard from "./pages/SeasonLeaderboard";
import InfoPage from "./pages/InfoPage";
import MobileNav from "./components/MobileNav";
import CommunityHome from "./pages/CommunityHome";
import NotFound from "./pages/NotFound";
import PublicProfile from "./pages/PublicProfile";
import Challenge from "./pages/Challenge";
import ReferralLanding from "./pages/ReferralLanding";
import ShareCenter from "./pages/ShareCenter";
import OrganizerSocialKit from "./pages/OrganizerSocialKit";
import RallyStore from "./pages/RallyStore";
import StoreSuccess from "./pages/StoreSuccess";
import StorePayPal from "./pages/StorePayPal";
import AdminCredits from "./pages/AdminCredits";
import AdminFarm from "./pages/AdminFarm";
import AdminLegal from "./pages/AdminLegal";
import { ForgotPassword, ResetPassword } from "./pages/PasswordReset";
import Seo from "./components/Seo";
import ErrorBoundary from "./components/ErrorBoundary";
import NotificationSettings from "./pages/NotificationSettings";
import NotificationCenter from "./pages/NotificationCenter";
import Unsubscribe from "./pages/Unsubscribe";
import AdminEmail from "./pages/AdminEmail";
import OrganizerProMax from "./pages/OrganizerProMax";
import OrganizerProMaxChannel from "./pages/OrganizerProMaxChannel";
import ProMaxBoard from "./pages/ProMaxBoard";
import ProMaxPayPal from "./pages/ProMaxPayPal";
import AdminProMax from "./pages/AdminProMax";
import ProPage from "./pages/ProPage";
import PromaxPage from "./pages/PromaxPage";
import PlayPage from "./pages/PlayPage";
import RallyFinder from "./pages/RallyFinder";
import PromaxSetupWizard from "./pages/PromaxSetupWizard";
import AdminCalendars from "./pages/AdminCalendars";
import SponsorPage from "./pages/SponsorPage";
import PromaxAccess from "./pages/PromaxAccess";
import PromaxDocument from "./pages/PromaxDocument";
import PromaxChannelWizard from "./pages/PromaxChannelWizard";
import PromaxNewDocument from "./pages/PromaxNewDocument";
import InstallPage from "./pages/InstallPage";
import AppSettings from "./pages/AppSettings";
import InstallBanner from "./components/install/InstallBanner";
import UpdatePrompt from "./components/install/UpdatePrompt";
import { Navigate, useParams } from "react-router-dom";
const BachecaRedirect = () => { const { code } = useParams(); return <Navigate to={`/promax/event/${code}`} replace />; };
import "./App.css";

function AppRouter() {
  const location = useLocation();
  const sl = new URLSearchParams(location.search).get("sl");
  if (sl) localStorage.setItem("rl_sl", sl);
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
      <Seo />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/onboarding" element={<OrganizerOnboarding />} />
        <Route path="/organizer/dashboard" element={<OrganizerDashboard />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        <Route path="/demo-paddock" element={<DemoPaddock />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="/paddock" element={<PaddockList />} />
        <Route path="/fantarally" element={<PaddockList mode="fanta" />} />
        <Route path="/community" element={<CommunityHome />} />
        <Route path="/u/:username" element={<PublicProfile />} />
        <Route path="/challenge/:code" element={<Challenge />} />
        <Route path="/ref/:code" element={<ReferralLanding />} />
        <Route path="/share-center" element={<ShareCenter />} />
        <Route path="/store" element={<RallyStore />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/daily" element={<DailyStage />} />
        <Route path="/daily/challenge/:code" element={<DailyChallenge />} />
        <Route path="/card" element={<RallyLoveCard />} />
        <Route path="/organizer/loyalty" element={<OrganizerLoyalty />} />
        <Route path="/admin/daily" element={<AdminDaily />} />
        <Route path="/admin/loyalty" element={<AdminLoyalty />} />
        <Route path="/farm" element={<RallyFarm />} />
        <Route path="/farm/team/:id" element={<FarmTeam />} />
        <Route path="/store/success" element={<StoreSuccess />} />
        <Route path="/store/paypal" element={<StorePayPal />} />
        <Route path="/admin/credits" element={<AdminCredits />} />
        <Route path="/admin/farm" element={<AdminFarm />} />
        <Route path="/admin/legal" element={<AdminLegal />} />
        <Route path="/admin/email" element={<AdminEmail />} />
        <Route path="/settings/notifications" element={<NotificationSettings />} />
        <Route path="/notifications" element={<NotificationCenter />} />
        <Route path="/unsubscribe" element={<Unsubscribe />} />
        <Route path="/organizer/promax" element={<OrganizerProMax />} />
        <Route path="/organizer/promax/new" element={<PromaxChannelWizard />} />
        <Route path="/organizer/promax/paypal" element={<ProMaxPayPal />} />
        <Route path="/organizer/promax/:cid" element={<OrganizerProMaxChannel />} />
        <Route path="/organizer/promax/:cid/documents/new" element={<PromaxNewDocument />} />
        <Route path="/promax" element={<PromaxPage />} />
        <Route path="/play" element={<PlayPage />} />
        <Route path="/organizer/promax/trova" element={<RallyFinder />} />
        <Route path="/organizer/promax/attiva/:eid" element={<PromaxSetupWizard />} />
        <Route path="/admin/promax/calendari" element={<AdminCalendars />} />
        <Route path="/promax/access" element={<PromaxAccess />} />
        <Route path="/promax/event/:code" element={<ProMaxBoard />} />
        <Route path="/promax/evento/:code" element={<ProMaxBoard />} />
        <Route path="/promax/event/:code/document/:did" element={<PromaxDocument />} />
        <Route path="/bacheca/:code" element={<BachecaRedirect />} />
        <Route path="/bacheche" element={<Navigate to="/promax/access" replace />} />
        <Route path="/pro" element={<ProPage />} />
        <Route path="/sponsor" element={<SponsorPage />} />
        <Route path="/install" element={<InstallPage />} />
        <Route path="/settings/app" element={<AppSettings />} />
        <Route path="/admin/promax" element={<AdminProMax />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/organizer/rallies/:id/social-kit" element={<OrganizerSocialKit />} />
        <Route path="/paddock/:id" element={<PaddockGame />} />
        <Route path="/season" element={<SeasonLeaderboard />} />
        <Route path="/p/:slug" element={<InfoPage />} />
        <Route path="/organizer/rallies/:id/game" element={<GameBuilder />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      <MobileNav />
      <InstallBanner />
      <UpdatePrompt />
    </>
  );
}

function App() {
  return (
    <I18nProvider>
      <ContentProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster theme="dark" position="top-right" richColors />
          <ErrorBoundary><AppRouter /></ErrorBoundary>
        </BrowserRouter>
      </AuthProvider>
      </ContentProvider>
    </I18nProvider>
  );
}

export default App;
